"use client";

import { useEffect, useState } from "react";

import type { AiKollenStickyCopy } from "./ai-kollen-copy";
import { trackAiKollenEvent } from "./ai-kollen-track";

type CcAkStickyCtaProps = {
  copy: AiKollenStickyCopy;
  /** Booking section id the bar scrolls to. */
  target: string;
  /** Element ids that hide the bar while on screen (hero CTA, booking, email). */
  hideWhenVisible: string[];
};

/**
 * Phone-only bar pinned to the bottom of the screen. It appears once the hero
 * button has scrolled away and hides again while the booking or the email
 * form is on screen, so the next step is always one tap away.
 */
export function CcAkStickyCta({ copy, target, hideWhenVisible }: CcAkStickyCtaProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const elements = hideWhenVisible
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => Boolean(element));
    if (!elements.length) return;
    const onScreen = new Set<Element>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) onScreen.add(entry.target);
        else onScreen.delete(entry.target);
      }
      setVisible(onScreen.size === 0);
    });
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [hideWhenVisible]);

  return (
    <div className="cc-ak-sticky" data-visible={visible ? "true" : "false"} aria-hidden={!visible}>
      <a
        href={`#${target}`}
        className="cc-ak-sticky__button"
        tabIndex={visible ? 0 : -1}
        onClick={() => trackAiKollenEvent("ai_kollen_cta", { placement: "sticky" })}
      >
        <span>{copy.cta}</span>
        <span className="cc-ak-sticky__note">{copy.note}</span>
      </a>
    </div>
  );
}
